/**
 * WinValueMietwagenImportCard — Issue #381
 * -----------------------------------------
 * Shown on a Gutachten-Akte that is linked to WinValue when no Mietwagen-Akte
 * exists yet (or after one has been created via the WinValue import flow).
 *
 * Rendering modes driven by deriveImportCardMode():
 *
 *   hidden  — no winvalue_uid: nothing rendered
 *   button  — winvalue_uid set, no mietwagen link: "Mietwagen-Akte anlegen" button
 *   success — mietwagen_akte_id is set: green success banner + 3 action cards
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Car, FileText, Key, CheckCircle2, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';
import {
  deriveImportCardMode,
  deriveAktionenStatus,
  type AktionStatus,
} from '@/lib/winvalue-mietwagen-import';

interface LinkedMwAkte {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  mietwagen_status: string | null;
  fahrzeug_id: string | null;
}

interface ActionCardProps {
  icon: React.ReactNode;
  label: string;
  status: AktionStatus;
  onClick: () => void;
}

function ActionCard({ icon, label, status, onClick }: ActionCardProps) {
  const isDone = status === 'erledigt';
  return (
    <button
      onClick={onClick}
      className={[
        'flex flex-col items-center gap-2 p-3 rounded-xl border text-center transition-colors',
        isDone
          ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
          : 'border-gray-200 bg-white hover:bg-gray-50',
      ].join(' ')}
    >
      <div
        className={[
          'w-8 h-8 rounded-lg flex items-center justify-center',
          isDone ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-500',
        ].join(' ')}
      >
        {icon}
      </div>
      <span className="text-[11px] font-semibold text-gray-800 leading-tight">{label}</span>
      <span
        className={[
          'text-[10px] font-medium px-2 py-0.5 rounded-full',
          isDone
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-amber-100 text-amber-700',
        ].join(' ')}
      >
        {isDone ? 'Erledigt ✓' : 'Offen'}
      </span>
    </button>
  );
}

export interface WinValueMietwagenImportCardProps {
  gutachtenAkteId: string;
  winvalueUid: string | null | undefined;
  winvalueRef?: string | null;
  mietwagenAkteId: string | null | undefined;
  onAkteUpdated?: () => void;
}

export default function WinValueMietwagenImportCard({
  gutachtenAkteId,
  winvalueUid,
  winvalueRef,
  mietwagenAkteId,
  onAkteUpdated,
}: WinValueMietwagenImportCardProps) {
  const [, navigate] = useLocation();
  const [isCreating, setIsCreating] = useState(false);
  const [linkedMwAkte, setLinkedMwAkte] = useState<LinkedMwAkte | null>(null);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [createdAkteNr, setCreatedAkteNr] = useState<string | null>(null);

  const mode = deriveImportCardMode(winvalueUid, mietwagenAkteId);

  // Fetch the linked Mietwagen-Akte details for action card status
  useEffect(() => {
    if (mode !== 'success' || !mietwagenAkteId) {
      setLinkedMwAkte(null);
      return;
    }

    let cancelled = false;
    setLoadingLinked(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('akten')
          .select('id, akten_nummer, akten_nr, mietwagen_status, fahrzeug_id')
          .eq('id', mietwagenAkteId)
          .maybeSingle();
        if (error) throw error;
        if (!cancelled) setLinkedMwAkte((data as LinkedMwAkte | null) ?? null);
      } catch {
        if (!cancelled) setLinkedMwAkte(null);
      } finally {
        if (!cancelled) setLoadingLinked(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, mietwagenAkteId]);

  if (mode === 'hidden') return null;

  async function handleCreate() {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const res = await fetch('/api/akte-aus-winvalue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'akteAusWinvalue',
          gutachten_akte_id: gutachtenAkteId,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setJustCreated(true);
      setCreatedAkteNr(json.mietwagen_akten_nummer ?? null);
      onAkteUpdated?.();
      toast.success('Mietwagen-Akte erstellt', {
        description: json.mietwagen_akten_nummer
          ? `Aktenzeichen: ${json.mietwagen_akten_nummer}`
          : 'Die Akte wurde erfolgreich angelegt.',
      });
    } catch (err) {
      toast.error('Fehler beim Erstellen der Mietwagen-Akte', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setIsCreating(false);
    }
  }

  function navigateToMwAkte(section: string) {
    if (!linkedMwAkte) return;
    const ident = aktenIdentifier(linkedMwAkte);
    if (!ident) return;
    navigate(buildAkteHref('mietwagen', ident, section));
  }

  if (mode === 'button') {
    const ref = winvalueRef ?? winvalueUid;
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
        <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Car className="w-3.5 h-3.5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-blue-900">Mietwagen-Akte anlegen</p>
          <p className="text-[11px] text-blue-700 mt-0.5">
            Direkt aus WinValue-Dossier{ref ? ` #${ref}` : ''} erstellen — alle Daten werden übernommen.
          </p>
        </div>
        <button
          onClick={handleCreate}
          disabled={isCreating}
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
        >
          {isCreating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Car className="w-3.5 h-3.5" />
          )}
          {isCreating ? 'Wird erstellt…' : 'Mietwagen-Akte anlegen'}
        </button>
      </div>
    );
  }

  // mode === 'success'
  const aktionenStatus = linkedMwAkte
    ? deriveAktionenStatus({
        mietwagen_status: linkedMwAkte.mietwagen_status as any,
        fahrzeug_id: linkedMwAkte.fahrzeug_id,
      })
    : { mietvertrag: 'offen' as AktionStatus, fahrzeug: 'offen' as AktionStatus, uebergabe: 'offen' as AktionStatus };

  const displayRef = createdAkteNr ?? (linkedMwAkte ? aktenIdentifier(linkedMwAkte) : null);
  const ref = winvalueRef ?? winvalueUid;

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
      {/* Success banner */}
      <div className="flex items-center gap-3">
        <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold text-emerald-900">
            Mietwagen-Akte aus WinValue-Dossier{ref ? ` #${ref}` : ''} erstellt ✓
          </p>
          {displayRef && (
            <p className="text-[11px] text-emerald-700 mt-0.5">Aktenzeichen: {displayRef}</p>
          )}
        </div>
        {linkedMwAkte && (() => {
          const ident = aktenIdentifier(linkedMwAkte);
          return ident ? (
            <button
              onClick={() => navigateToMwAkte('uebersicht')}
              className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-1 flex-shrink-0"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Zur Akte
            </button>
          ) : null;
        })()}
      </div>

      {/* Action cards */}
      {loadingLinked ? (
        <div className="flex items-center gap-2 text-[12px] text-emerald-700 py-1">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Lade Aktionen…
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <ActionCard
            icon={<FileText className="w-4 h-4" />}
            label="Mietvertrag erstellen"
            status={aktionenStatus.mietvertrag}
            onClick={() => navigateToMwAkte('mietvertrag')}
          />
          <ActionCard
            icon={<Car className="w-4 h-4" />}
            label="Fahrzeug zuweisen"
            status={aktionenStatus.fahrzeug}
            onClick={() => navigateToMwAkte('uebersicht')}
          />
          <ActionCard
            icon={<Key className="w-4 h-4" />}
            label="Übergabe protokollieren"
            status={aktionenStatus.uebergabe}
            onClick={() => navigateToMwAkte('uebergabe')}
          />
        </div>
      )}

      {/* Warning when actions still open after 24h */}
      {!loadingLinked && linkedMwAkte && (() => {
        // Show warning hint for open actions — actual 24h tracking is server-side
        const allDone =
          aktionenStatus.mietvertrag === 'erledigt' &&
          aktionenStatus.fahrzeug === 'erledigt' &&
          aktionenStatus.uebergabe === 'erledigt';
        if (allDone) return null;
        return (
          <div className="flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            Offene Aktionen bitte zeitnah erledigen, damit der Fall vollständig abgeschlossen werden kann.
          </div>
        );
      })()}
    </div>
  );
}
